import React, { useState, useEffect, useCallback, useRef } from "react";
import { Snackbar, Alert, Box } from "@mui/material";
import { CheckCircle, Warning, Error as ErrorIcon, Info } from "@mui/icons-material";

import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

import { getUsersWithRolesService } from "modules/usuario/services/userService";
import TablaPermisosUsuarios from "./components/TablaPermisosUsuarios";
import ModalPermisos from "./components/ModalPermisos";
import HeaderPermisos from "./components/HeaderPermisos";
import { usePermisos } from "./hooks/usePermisos";

const COPROPIEDAD_ROLE_NAME = "copropiedad";
const ROLES_HABILITADOS = new Set([
  "ADMIN NOC",
  "ANALISTA NOC",
  "ASEGURAMIENTO",
  "ADMIN",
  "SAC",
  "PLANEACION",
]);

const Permisos = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    autoHideDuration: 6000,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editUsuarioId, setEditUsuarioId] = useState(null);
  const [editUsuario, setEditUsuario] = useState(null);

  const tableRef = useRef();

  const {
    usuarios,
    setUsuarios,
    propiedades,
    permisos,
    permisosData,
    loading,
    error,
    isSubmitting,
    todosLosPermisos,
    loadingAllPermisos,
    loadAllPermisos,
    loadPermisosUsuario,
    guardarPermisos,
    eliminarPermisos,
  } = usePermisos();

  const showNotification = useCallback((message, severity = "success", duration = 6000) => {
    setSnackbar({ open: true, message, severity, autoHideDuration: duration });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    const loadUsuarios = async () => {
      try {
        const result = await getUsersWithRolesService();
        const usuariosFiltrados = result.filter((usuario) => {
          const roleName = usuario.role?.name;
          if (typeof roleName !== "string") return false;
          const normalizedRole = roleName
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim()
            .replace(/\s+/g, " ")
            .toUpperCase();
          return (
            ROLES_HABILITADOS.has(normalizedRole) ||
            normalizedRole.toLowerCase().includes(COPROPIEDAD_ROLE_NAME)
          );
        });
        setUsuarios(usuariosFiltrados);
        if (usuariosFiltrados.length === 0) {
          showNotification("No hay usuarios con roles habilitados para Copropiedades", "info");
        }
      } catch (err) {
        console.error("Error al cargar usuarios:", err);
        showNotification("Error al cargar usuarios", "error");
      }
    };
    loadUsuarios();
  }, [showNotification, setUsuarios]);

  const handleLoadPermisos = useCallback(
    async (userId) => {
      if (!userId) return {};
      const result = await loadPermisosUsuario(userId);
      return result || {};
    },
    [loadPermisosUsuario]
  );

  // 🔥 NUEVO: Acepta permisosAEliminar
  const handleGuardarPermisos = useCallback(
    async (userId, permisosData, permisosAEliminar) => {
      const result = await guardarPermisos(userId, permisosData, permisosAEliminar);
      if (result.success) {
        showNotification(result.message, "success");
        await loadAllPermisos();
      } else {
        showNotification(result.error || "Error al guardar permisos", "error");
      }
      return result;
    },
    [guardarPermisos, loadAllPermisos, showNotification]
  );

  const handleRefreshTable = useCallback(async () => {
    await loadAllPermisos();
    showNotification("Datos actualizados correctamente", "success");
  }, [loadAllPermisos, showNotification]);

  const handleOpenCreateModal = useCallback(() => {
    setEditUsuarioId(null);
    setEditUsuario(null);
    setModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((userId, usuario) => {
    setEditUsuarioId(userId);
    setEditUsuario(usuario);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(
    (shouldRefresh = false) => {
      setModalOpen(false);
      setEditUsuarioId(null);
      setEditUsuario(null);
      if (shouldRefresh) {
        handleRefreshTable();
      }
    },
    [handleRefreshTable]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%", maxWidth: "500px", borderRadius: "8px" }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <Warning fontSize="inherit" />,
            info: <Info fontSize="inherit" />,
          }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {snackbar.message}
          </MDTypography>
        </Alert>
      </Snackbar>

      <MDBox py={3}>
        <HeaderPermisos>
          <TablaPermisosUsuarios
            ref={tableRef}
            permisosData={todosLosPermisos}
            usuarios={usuarios}
            loading={loadingAllPermisos}
            error={error}
            onRefresh={handleRefreshTable}
            onAdd={handleOpenCreateModal}
            onEdit={handleOpenEditModal}
          />
        </HeaderPermisos>
      </MDBox>

      <ModalPermisos
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleGuardarPermisos}
        onLoadPermisos={handleLoadPermisos}
        usuarios={usuarios}
        propiedades={propiedades}
        permisosData={permisosData}
        usuarioId={editUsuarioId}
        loading={loading}
        isSubmitting={isSubmitting}
        titulo={
          editUsuarioId
            ? `Editar permisos de ${editUsuario?.username || editUsuario?.name || "Usuario"}`
            : "Crear Nuevos Permisos"
        }
      />
    </DashboardLayout>
  );
};

export default Permisos;
